/* eslint-disable no-nested-ternary */
import { useCallback, useEffect, useState, useMemo } from 'react';
import {
  generatePath,
  useMatch,
  useNavigate,
  useParams,
  useOutletContext,
  useLocation,
} from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { SendMessage } from 'react-use-websocket';
import queryString from 'query-string';
import isEqual from 'lodash.isequal';
import {
  PermissionEnum,
  IconSelected,
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  IconRight,
  IconUpload,
  ButtonPrimary,
  DateInputRange,
  IconCalendar,
  Table,
  SidePanelLoader,
  BlacklistData,
  BlacklistDataContent,
  ResponseError,
  ChangeLogs,
  PermissionDenied,
  BlacklistUserInfoData,
  BlacklistUserInfo,
  formatBlacklistItem,
  Select,
  InputNumber,
  responseErrorsResolver,
  Popup,
  OnSelectChange,
  usePrevious,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { useForm } from 'react-hook-form';
import RealMonthlyVolumes from '../../../components/partial/aml/real-monthly-volumes';
import { getAmlGeneralInfo, clearAmlChangeLogs } from '../store/slices/aml/aml';
import {
  useLazyGetIndividualUserScoreQuery,
  useCreateIndividualUserScoreMutation,
  useLazyGetIndividualAmlUpdatingItemQuery,
  useLazyGetIndividualLastCalculatingScoreQuery,
  useUpdateIndividualRangesMutation,
  useLazyGetIndividualOngoingMonitoringQuery,
  useLazyGetIndividualOngoingMonitoringItemQuery,
  useApplyIndividualOngoingWarningActionMutation,
  useBatchApplyIndividualOngoingWarningActionMutation,
  useResetIndividualWarningGroupActionMutation,
  useLazyGetIndividualWarningGroupWithPaginationQuery,
  useLazyGetIndividualOngoingWarningQuery,
  useCreateIndividualManualRiskScoreMutation,
  useUpdateIndividualUserBaseInformationMutation,
} from '../store/services/individual';
import { useLazyGetCustomersApplicationSchemesQuery } from '../../../store/dictionaries/services';
import {
  useGetOngoingMoitoringPdfMutation,
  useLazyGetCustomersChangeLogsQuery,
  useLazyGetCustomersAMLScoresHistoryQuery,
  useUpdateCustomerQuestionnaireMutation,
  useLazyGetCustomersSourceOfFundsQuery,
  useLazyGetCustomersSourceOfIncomeDictionaryQuery,
  useLazyGetCustomersSourceOfWealthQuery,
  useLazyGetCustomersSourceOfWealthForIndividualDictionaryQuery,
  useLazyGetIndividualCustomersAccountsCurrenciesQuery,
  useCreateIndividualCustomersAccountsCurrencyMutation,
  useUpdateIndividualCustomersAccountsCurrencyMutation,
  useDeleteIndividualCustomersAccountsCurrencyMutation,
  useLazyGetIndividualCustomersAccountsFlowsQuery,
  useCreateIndividualCustomersAccountsFlowMutation,
  useUpdateIndividualCustomersAccountsFlowMutation,
  useDeleteIndividualCustomersAccountsFlowMutation,
  useLazyGetIndividualCustomersExpectedFundsOriginsQuery,
  useCreateIndividualCustomersExpectedFundsOriginsMutation,
  useUpdateIndividualCustomersExpectedFundsOriginsMutation,
  useDeleteIndividualCustomersExptectedFundsOriginsMutation,
  useLazyGetCustomersCRSAndFactaQuery,
  useCreateCustomersCRSAndFactaMutation,
  useUpdateCustomersCRSAndFactaMutation,
  useDeleteCustomersCRSAndFactaMutation,
  useLazyGetCOFCustomersCRSAndFactaQuery,
  useCreateCOFCustomersCRSAndFactaMutation,
  useUpdateCOFCustomersCRSAndFactaMutation,
  useDeleteCOFCustomersCRSAndFactaMutation,
  useLazyGetCustomersAffilatedCompaniesQuery,
  useCreateCustomersAffilatedCompaniesMutation,
  useUpdateCustomersAffilatedCompaniesMutation,
  useDeleteCustomersAffilatedCompaniesMutation,
} from '../../customers/store/services/customers';
import Card from '../../../components/partial/card/card';
import AmlGeneralInfo from '../../../components/individual/aml/general-info';
import RiskScoring from '../../../components/individual/aml/risk-scoring';
import AmlAccountsTransactionStatement from '../../customers/aml-statements/aml-statements';
import OngoingMonitoring from '../../customers/ongoing-monitoring/ongoing-monitoring';
import OngoingPanelContent from '../../customers/ongoing-monitoring/ongoing-panel-content';
import {
  ApplicationScheme,
  COFAndIndividualQuestionnarieFormValues,
  CustomerRanges,
  CustomerScore,
  CustomerScoreUpdateStatus,
  CustomerType,
  OngoingMonitring,
  OrganizationType,
  Questionnarie,
} from '../../customers/types';
import { ongoingColumns, ongoingColumnsLabels } from './variables/ongoing';
import formatOngoingMonitroing from '../helpers/format-ongoing-monitoring';
import AdditionalInfo from '../../../components/individual/aml/additional-information';
import { setNotificationVisible } from '../../../../../../store/slice';
import AmlChangeLogs from '../../customers/aml-change-logs/change-logs';
import { CddFormValues } from '../../../components/corporate/types';
import {
  AccountsCurrencies,
  AccountsCurrenciesFormValues,
  AccountsFlowsFormValues,
  AffilatedCompanyFormValues,
  CRSAndFatcaFormValues,
  ExpectedFundsOriginsFormValues,
} from '../../new-application/types';
import getIndividualRegulatedCompanyQuestionnaireBody from './helpers/get-individual-regulated-company-questionnarie';

import ROUTES from '../../../routes';

import cn from '../information.module.css';
import { BaseInfoFormValues } from '../types';

let timeInterval: NodeJS.Timeout;
let manualRiskScoreTimeout: NodeJS.Timeout;

const Aml = (): React.JSX.Element => {
  const {
    individualCustomers: {
      info: {
        general: { baseInformation, changeLogs, changeLogsPagination },
        aml: {
          amlGeneralInfo,
          aml,
          amlUpdateStatus,
          nextReviewDate,
          ongoingMonitoring,
          ongoingMonitoringItem,
          amlChangeLogs,
          amlChangeLogsPagination,
          accountsCurrencies,
          accountsFlows,
          expectedFundsOrigins,
          crsAndFatca,
          sourceOfWealth,
          sourceOfIncome,
          sourceOfWealthForIndividual,
          customerSourceOfFunds,
          affiliatedCompanies,
        },
      },
    },
    customersDictionaries: {
      cardsEnabled,
      currentEmployee,
      riskScoreRanges,
      applicationSchemes,
      clientsActivities,
    },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);
  const {
    activePermissions,
    baseCurrency,
    countriesList,
    currenciesList: currencies,
    currencies: currenciesList,
  } = useAppSelector((state) => state.root);

  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [exportPdfPanelVisible, setExportPdfPanelVisible] = useState(false);
  const [showOngoing, setShowOngoing] = useState(false);
  const [ongoingItemPanelVisible, setOngoingItemPanelVisible] = useState(false);
  const [blacklistDataVisible, setBlacklistDataVisible] = useState(false);
  const [blacklistData, setBlacklistData] = useState<BlacklistData | undefined>(
    undefined,
  );
  const [blacklistItemVisible, setBlacklistItemVisible] = useState(false);
  const [blacklistItem, setBlacklistDataItem] = useState<
    BlacklistUserInfoData | undefined
  >(undefined);
  const [applyError, setApplyError] = useState('');
  const [changeLogsPanelIsOpen, setChangeLogPanelIsOpen] = useState(false);
  const [selectedWarningId, setSelectedWarningId] = useState('');
  const [selectedWarningGroup, setSelectedWarningGroup] = useState('');
  const [totalRiskScore, setTotalRiskScore] = useState(0);
  const [totalRiskScoreLevel, setTotalRiskScoreLevel] = useState('');
  const [manualScoreSidepanelVisible, setManualScoreSidepanelVisible] =
    useState(false);
  const [manualRiskScoreError, setManualRiskScoreError] = useState('');
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [
    riskScoreChangeLogsSidepanelVisible,
    setRiskScoreChangeLogsSidepanelVisible,
  ] = useState(false);
  const [defaultCddValues, setDefaultCddValues] = useState<CddFormValues>({});
  const [cddFormValues, setCddFormValues] = useState<CddFormValues>({});
  const [defaultQuestionnaire, setDefaultQuestionaire] =
    useState<Questionnarie>();
  const [currentApplicationScheme, setCurrentApplicationScheme] = useState<
    ApplicationScheme | undefined
  >(undefined);
  const [accountsCurrenciesValues, setAccountsCurrenciesValues] = useState<
    AccountsCurrenciesFormValues[]
  >([
    {
      id: 'accounts-currency-1',
      accountOpeningPurpose: '',
      currencyId: '',
      plannedTransactionNature: '',
    },
  ]);
  const [defaultOrganizationValues, setDefaultOrganizationValues] =
    useState<Partial<BaseInfoFormValues>>();
  const [selectedAccountsCurrencyId, setSelectedAccountsCurrencyId] =
    useState('');
  const [accountsFlowsValues, setAccountsFlowsValues] = useState<
    AccountsFlowsFormValues[]
  >([]);
  const [expectedFundsOriginsValues, setExpectedFundsOriginsValues] = useState<
    ExpectedFundsOriginsFormValues[]
  >([
    {
      id: 'expected-funds-origins-1',
      currencyId: '',
      mainCounterpartiesAndWebsites: '',
      countryOfIncorporationId: '',
      counterpartyBankCountryIds: [],
      activityCountryIds: [],
      mainActivities: '',
      transactionReason: '',
      transactionType: 'INCOMING',
    },
    {
      id: 'expected-funds-origins-2',
      currencyId: '',
      mainCounterpartiesAndWebsites: '',
      countryOfIncorporationId: '',
      counterpartyBankCountryIds: [],
      activityCountryIds: [],
      mainActivities: '',
      transactionReason: '',
      transactionType: 'OUTGOING',
    },
  ]);
  const [selectedExpectedFundsOriginsId, setSelectedFundsOriginsId] =
    useState('');
  const [crsAndFatcaValues, setCrsAndFatcaValues] = useState<
    CRSAndFatcaFormValues[]
  >([
    {
      id: 'crs-and-fatca-1',
      residenceCountryId: '',
      taxIdentificationNumber: '',
      noTinReason: '',
    },
  ]);
  const [selectedCrsAndFatcaId, setSelectedCrsAndFatcaId] = useState('');
  const [affilatedCompaniesValues, setAffilatedCompaniesValues] = useState<
    AffilatedCompanyFormValues[]
  >([
    {
      id: 'affilated-company-1',
      name: '',
      registrationNumber: '',
      mainActivities: '',
      position: '',
      countryId: '',
      percentageOfOwnership: '',
    },
  ]);
  const [selectedAffilatedCompanyId, setSelectedAffilatedCompanyId] =
    useState('');

  const isDirty = !isEqual(defaultCddValues, cddFormValues);

  const [exportDate, setExportDate] = useState<string[]>([]);

  const employmentActivity = clientsActivities?.find(
    (item) => item.name === 'Employment',
  );
  const otherActivity = clientsActivities?.find(
    (item) => item.name === 'Other',
  );
  const businessOwnerActivity = clientsActivities?.find(
    (item) => item.name === 'Business Owner',
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
    clearErrors,
    setError,
  } = useForm<Questionnarie>();

  const {
    control: organizationControl,
    reset: organizationReset,
    formState: { errors: organizationErrors },
    watch: organizationWatch,
    getValues,
    setError: organizationSetError,
    clearErrors: organizationClearErrors,
    setValue: organizationSetValue,
  } = useForm<Partial<BaseInfoFormValues>>();

  const questionnaireFields = watch();
  const organizationFields = organizationWatch();

  const prevClientActivitiy = usePrevious(organizationFields.clientActivityId);

  const match = useMatch(ROUTES.CUSTOMERS_INDIVIDUAL_AML);
  const { customerId } = useParams();

  const [getIndividualScore, { isLoading }] =
    useLazyGetIndividualUserScoreQuery();
  const [getIndividualAmlUpdatingItem] =
    useLazyGetIndividualAmlUpdatingItemQuery();
  const [getIndividualLastCalculaingScore] =
    useLazyGetIndividualLastCalculatingScoreQuery();
  const [createIndividualScore, { isSuccess, isLoading: requestInProgress }] =
    useCreateIndividualUserScoreMutation();
  const [
    getIndividualOngoingMonitoring,
    { isLoading: ongoingIsLoading, isFetching: ongoingIsFetching },
  ] = useLazyGetIndividualOngoingMonitoringQuery();
  const [
    getIndividualOngoingMonitoringItem,
    { isLoading: ongoingItemIsLoading, isFetching: ongoingItemIsFetching },
  ] = useLazyGetIndividualOngoingMonitoringItemQuery();
  const [getApplicationsSchemes] = useLazyGetCustomersApplicationSchemesQuery();

  const [updateIndividualRanges, { isLoading: updateRangesInProgress }] =
    useUpdateIndividualRangesMutation();

  const { search } = useLocation();

  const [applyIndividualOngoingWarning, { isLoading: applyWarningInProgress }] =
    useApplyIndividualOngoingWarningActionMutation();
  const [
    batchApplyIndividualOngoingWarning,
    {
      isLoading: batchApplyWarningInProgress,
      isSuccess: batchApplyWarningIsSuccess,
    },
  ] = useBatchApplyIndividualOngoingWarningActionMutation();
  const [
    getOngoingMonitoringPdf,
    {
      isLoading: ongoingMonitoringPdfIsLoading,
      isSuccess: ongoingMonitoringPdfIsSuccess,
    },
  ] = useGetOngoingMoitoringPdfMutation();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetCustomersChangeLogsQuery();
  const [
    resetIndividualTransactionWarning,
    {
      isLoading: resetTransactionsWarningIsLoading,
      isSuccess: resetTransactionsWarningIsSuccess,
    },
  ] = useResetIndividualWarningGroupActionMutation();
  const [
    getIndividualWarning,
    {
      isLoading: paginationInProgress,
      isFetching: paginationIsFetching,
      isSuccess: paginationIsSuccess,
    },
  ] = useLazyGetIndividualWarningGroupWithPaginationQuery();
  const [
    getIndividualOngoingWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetIndividualOngoingWarningQuery();
  const [
    createIndividualManualRiskScore,
    {
      isLoading: createRiskScoreIsLoading,
      isSuccess: createRiskScoreIsSuccess,
    },
  ] = useCreateIndividualManualRiskScoreMutation();
  const [
    getCustomerAmlScoresHistory,
    {
      isLoading: amlScoresHistoryIsLoading,
      isFetching: amlScoresHistoryIsFetching,
    },
  ] = useLazyGetCustomersAMLScoresHistoryQuery();
  const [updateQuestionnaire, { isLoading: questionnnaireRequestInProgress }] =
    useUpdateCustomerQuestionnaireMutation();

  const [getCustomersSourceOfFundsDictionary] =
    useLazyGetCustomersSourceOfFundsQuery();
  const [getCustomersSourceOfWealth] = useLazyGetCustomersSourceOfWealthQuery();
  const [getCustomersSourceOfIncome] =
    useLazyGetCustomersSourceOfIncomeDictionaryQuery();
  const [getCustomersSourceOfWealthForIndividua] =
    useLazyGetCustomersSourceOfWealthForIndividualDictionaryQuery();
  const [
    getAccountsCurrencies,
    {
      isLoading: accountsCurrenciesIsLoading,
      isFetching: accountsCurrenciesIsFetching,
    },
  ] = useLazyGetIndividualCustomersAccountsCurrenciesQuery();
  const [
    createAccountsCurrencies,
    { isLoading: createAccountsCurrenciesIsProgress },
  ] = useCreateIndividualCustomersAccountsCurrencyMutation();
  const [
    updateAccountsCurrencies,
    { isLoading: updateAccountsCurrenciesInProgress },
  ] = useUpdateIndividualCustomersAccountsCurrencyMutation();
  const [
    deleteAccountsCurrencies,
    {
      isLoading: deleteAccountsCurrenciesInProgress,
      isSuccess: deleteAccountsCurrenciesIsSuccess,
    },
  ] = useDeleteIndividualCustomersAccountsCurrencyMutation();
  const [
    getAccountsFlows,
    { isLoading: accountsFlowsIsLoading, isFetching: accountsFlowsIsFetching },
  ] = useLazyGetIndividualCustomersAccountsFlowsQuery();
  const [createAccountsFlows, { isLoading: createAccountsFlowsIsProgress }] =
    useCreateIndividualCustomersAccountsFlowMutation();
  const [updateAccountsFlows, { isLoading: updateAccountsFlowsInProgress }] =
    useUpdateIndividualCustomersAccountsFlowMutation();
  const [deleteAccountsFlows] =
    useDeleteIndividualCustomersAccountsFlowMutation();
  const [
    getExpectedFundsOrigins,
    {
      isLoading: expectedFundsOriginsIsLoading,
      isFetching: expectedFundsOriginsIsFetching,
    },
  ] = useLazyGetIndividualCustomersExpectedFundsOriginsQuery();
  const [
    createExpectedFundsOrigins,
    { isLoading: createExpectedFundsOriginsInProgress },
  ] = useCreateIndividualCustomersExpectedFundsOriginsMutation();
  const [
    updateExpectedFundsOrigins,
    { isLoading: updateExpectedFundsOriginsInProgress },
  ] = useUpdateIndividualCustomersExpectedFundsOriginsMutation();
  const [
    deleteExpectedFundsOrigins,
    {
      isLoading: deleteExpectedFundsOriginsInProgress,
      isSuccess: deleteExpectedFundsOriginsIsSuccess,
    },
  ] = useDeleteIndividualCustomersExptectedFundsOriginsMutation();
  const [
    getCOFCrsAndFatca,
    {
      isLoading: cofCrsAndFatcaIsLoading,
      isFetching: cofCrsAndFatcaIsFetching,
    },
  ] = useLazyGetCOFCustomersCRSAndFactaQuery();
  const [createCOFCrsAndFatca, { isLoading: createCofCrsAndFatcaIsProgress }] =
    useCreateCOFCustomersCRSAndFactaMutation();
  const [updateCOFCrsAndFatca, { isLoading: updateCofCrsAndFatcaInProgress }] =
    useUpdateCOFCustomersCRSAndFactaMutation();
  const [
    deleteCOFCrsAndFatca,
    {
      isLoading: deleteCofCrsAndFatcaInProgress,
      isSuccess: deleteCofCrsAndFatcaIsSuccess,
    },
  ] = useDeleteCOFCustomersCRSAndFactaMutation();
  const [
    getCrsAndFatca,
    { isLoading: crsAndFatcaIsLoading, isFetching: crsAndFatcaIsFetching },
  ] = useLazyGetCustomersCRSAndFactaQuery();
  const [createCrsAndFatca, { isLoading: createCrsAndFatcaIsProgress }] =
    useCreateCustomersCRSAndFactaMutation();
  const [updateCrsAndFatca, { isLoading: updateCrsAndFatcaInProgress }] =
    useUpdateCustomersCRSAndFactaMutation();
  const [
    deleteCrsAndFatca,
    {
      isLoading: deleteCrsAndFatcaInProgress,
      isSuccess: deleteCrsAndFatcaIsSuccess,
    },
  ] = useDeleteCustomersCRSAndFactaMutation();
  const [updateIndividualInformation, { isLoading: informationIsUpdate }] =
    useUpdateIndividualUserBaseInformationMutation();
  const [
    getAffilatedCompanies,
    {
      isLoading: affilatedCompaniesIsLoading,
      isFetching: affilatedCompaniesIsFetching,
    },
  ] = useLazyGetCustomersAffilatedCompaniesQuery();
  const [
    createAffilatedCompany,
    { isLoading: createAffilatedCompanyInProgress },
  ] = useCreateCustomersAffilatedCompaniesMutation();
  const [
    updateAffilatedCompany,
    { isLoading: updateAffilatedCompanyInProgress },
  ] = useUpdateCustomersAffilatedCompaniesMutation();
  const [
    deleteAffilatedCompany,
    {
      isLoading: deleteAffilatedCompanyInProgress,
      isSuccess: deleteAffilatedCompanyIsSuccess,
    },
  ] = useDeleteCustomersAffilatedCompaniesMutation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { sendMessage } = useOutletContext<{
    sendMessage: SendMessage | null;
  }>();

  const onSaveExpectedFundsOrigins = async () => {
    const expectedFundsOriginsForCreation: ExpectedFundsOriginsFormValues[] =
      [];
    const expectedFundsOriginsForUpdate: ExpectedFundsOriginsFormValues[] = [];

    expectedFundsOriginsValues?.forEach((item) => {
      if (item.id?.includes('expected-funds-origins')) {
        expectedFundsOriginsForCreation.push(item);
      } else {
        const currentExpectedFundsOrigins = expectedFundsOrigins?.find(
          (value) => value.id === item.id,
        );

        if (currentExpectedFundsOrigins) {
          if (
            !isEqual(item, {
              id: currentExpectedFundsOrigins.id,
              currencyId: currentExpectedFundsOrigins.currencyId,
              mainCounterpartiesAndWebsites:
                currentExpectedFundsOrigins.mainCounterpartiesAndWebsites,
              countryOfIncorporationId:
                currentExpectedFundsOrigins.countryOfIncorporationId,
              counterpartyBankCountryIds:
                currentExpectedFundsOrigins.counterpartyBankCountryIds,
              activityCountryIds:
                currentExpectedFundsOrigins.activityCountryIds,
              mainActivities: currentExpectedFundsOrigins.mainActivities,
              transactionReason: currentExpectedFundsOrigins.transactionReason,
              transactionType: currentExpectedFundsOrigins.transactionType,
            })
          ) {
            expectedFundsOriginsForUpdate.push(item);
          }
        }
      }
    });

    if (expectedFundsOriginsForCreation?.length) {
      await Promise.all(
        expectedFundsOriginsForCreation
          ?.filter((item) => item.currencyId)
          ?.map(async (item) => {
            const response = await createExpectedFundsOrigins({
              id: baseInformation?.questionnaire?.id as string,
              body: {
                currencyId: item.currencyId,
                mainCounterpartiesAndWebsites:
                  item.mainCounterpartiesAndWebsites || '',
                countryOfIncorporationId: item.countryOfIncorporationId || '',
                counterpartyBankCountryIds:
                  item.counterpartyBankCountryIds || '',
                activityCountryIds: item.activityCountryIds || '',
                mainActivities: item.mainActivities || '',
                transactionReason: item.transactionReason || '',
                transactionType: item.transactionType || '',
              },
            });

            return response;
          }),
      );
    }

    if (expectedFundsOriginsForUpdate?.length) {
      await Promise.all(
        expectedFundsOriginsForUpdate?.map(async (item) => {
          const response = await updateExpectedFundsOrigins({
            id: item.id as string,
            body: {
              currencyId: item.currencyId,
              mainCounterpartiesAndWebsites:
                item.mainCounterpartiesAndWebsites || '',
              countryOfIncorporationId: item.countryOfIncorporationId || '',
              counterpartyBankCountryIds: item.counterpartyBankCountryIds || '',
              activityCountryIds: item.activityCountryIds || '',
              mainActivities: item.mainActivities || '',
              transactionReason: item.transactionReason || '',
              transactionType: item.transactionType || '',
            },
          });

          return response;
        }),
      );
    }
  };

  const onSaveAccountsCurrencies = async () => {
    const accountsCurrenciesForCreation: AccountsCurrenciesFormValues[] = [];
    const accountsCurrenciesForUpdate: AccountsCurrenciesFormValues[] = [];

    accountsCurrenciesValues?.forEach((item) => {
      if (item.id?.includes('accounts-currency')) {
        accountsCurrenciesForCreation.push(item);
      } else {
        const currentAccountsCurrency = accountsCurrencies?.find(
          (currency) => currency.id === item.id,
        );

        if (currentAccountsCurrency) {
          if (
            !isEqual(item, {
              id: currentAccountsCurrency.id,
              currencyId: currentAccountsCurrency.currencyId,
              accountOpeningPurpose:
                currentAccountsCurrency.accountOpeningPurpose,
              plannedTransactionNature:
                currentAccountsCurrency.plannedTransactionNature,
            })
          ) {
            accountsCurrenciesForUpdate.push(item);
          }
        }
      }
    });

    if (accountsCurrenciesForCreation?.length) {
      await Promise.all(
        accountsCurrenciesForCreation
          ?.filter((item) => item.currencyId)
          ?.map(async (item) => {
            const response = await createAccountsCurrencies({
              id: baseInformation?.questionnaire?.id as string,
              body: {
                currencyId: item.currencyId,
                accountOpeningPurpose: item.accountOpeningPurpose || '',
                plannedTransactionNature: item.plannedTransactionNature || '',
              },
            });

            return response;
          }),
      );
    }

    if (accountsCurrenciesForUpdate?.length) {
      await Promise.all(
        accountsCurrenciesForUpdate?.map(async (item) => {
          const response = await updateAccountsCurrencies({
            id: item.id as string,
            body: {
              currencyId: item.currencyId,
              accountOpeningPurpose: item.accountOpeningPurpose || '',
              plannedTransactionNature: item.plannedTransactionNature || '',
            },
          });

          return response;
        }),
      );
    }
  };

  const onSaveAccountsFlows = async () => {
    const accountsFlowsForCreation: AccountsFlowsFormValues[] = [];
    const accountsFlowsForUpdate: AccountsFlowsFormValues[] = [];

    accountsFlowsValues?.forEach((item) => {
      if (item.id?.includes('accounts-flow')) {
        accountsFlowsForCreation.push(item);
      } else {
        const currentAccountsFlow = accountsFlows?.find(
          (flow) => flow.id === item.id,
        );

        if (currentAccountsFlow) {
          if (
            !isEqual(item, {
              id: currentAccountsFlow.id,
              transactionType: currentAccountsFlow.transactionType,
              currencyId: currentAccountsFlow.currencyId,
              expectedAnnualCreditTurnover:
                currentAccountsFlow.expectedAnnualCreditTurnover.toString(),
              maxTransferAmountPerTransaction:
                currentAccountsFlow.maxTransferAmountPerTransaction.toString(),
              transactionFrequency:
                currentAccountsFlow.transactionFrequency.toString(),
              transactionFrequencyPeriodMonths:
                currentAccountsFlow.transactionFrequencyPeriodMonths.toString(),
            })
          ) {
            accountsFlowsForUpdate.push(item);
          }
        }
      }
    });

    if (
      accountsFlowsForCreation?.filter(
        (item) =>
          item.expectedAnnualCreditTurnover &&
          item.maxTransferAmountPerTransaction &&
          item.transactionFrequency &&
          item.transactionFrequencyPeriodMonths,
      )?.length
    ) {
      await Promise.all(
        accountsFlowsForCreation
          ?.filter(
            (item) =>
              item.expectedAnnualCreditTurnover &&
              item.maxTransferAmountPerTransaction &&
              item.transactionFrequency &&
              item.transactionFrequencyPeriodMonths,
          )
          ?.map(async (item) => {
            const response = await createAccountsFlows({
              id: baseInformation?.questionnaire?.id as string,
              body: {
                transactionType: item.transactionType,
                currencyId: item.currencyId,
                expectedAnnualCreditTurnover: Number(
                  item.expectedAnnualCreditTurnover,
                ),
                maxTransferAmountPerTransaction: Number(
                  item.maxTransferAmountPerTransaction,
                ),
                transactionFrequency: Number(item.transactionFrequency),
                transactionFrequencyPeriodMonths: Number(
                  item.transactionFrequencyPeriodMonths,
                ),
              },
            });

            return response;
          }),
      );
    }

    if (accountsFlowsForUpdate?.length) {
      await Promise.all(
        accountsFlowsForUpdate?.map(async (item) => {
          const response = await updateAccountsFlows({
            id: item.id as string,
            body: {
              transactionType: item.transactionType,
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: Number(
                item.expectedAnnualCreditTurnover,
              ),
              maxTransferAmountPerTransaction: Number(
                item.maxTransferAmountPerTransaction,
              ),
              transactionFrequency: Number(item.transactionFrequency),
              transactionFrequencyPeriodMonths: Number(
                item.transactionFrequencyPeriodMonths,
              ),
            },
          });

          return response;
        }),
      );
    }
  };

  const onSaveAffilatedCompanies = async () => {
    const affilatedCompaniesForCreate: AffilatedCompanyFormValues[] = [];
    const affilatedCompaniesForForUpdate: AffilatedCompanyFormValues[] = [];

    affilatedCompaniesValues?.forEach((item) => {
      if (item.id?.includes('affilated-company')) {
        affilatedCompaniesForCreate.push(item);
      } else {
        const currentAffilatedCompany = affiliatedCompanies?.find(
          (value) => value.id === item.id,
        );

        if (currentAffilatedCompany) {
          if (
            !isEqual(item, {
              id: currentAffilatedCompany.id,
              name: currentAffilatedCompany.name,
              registrationNumber: currentAffilatedCompany.registrationNumber,
              mainActivities: currentAffilatedCompany.mainActivities,
              position: currentAffilatedCompany.position,
              countryId: currentAffilatedCompany.countryId,
              percentageOfOwnership:
                currentAffilatedCompany.percentageOfOwnership,
            })
          ) {
            affilatedCompaniesForForUpdate.push(item);
          }
        }
      }
    });

    if (affilatedCompaniesForCreate?.length) {
      await Promise.all(
        affilatedCompaniesForCreate
          ?.filter(
            (item) =>
              item.name &&
              item.registrationNumber &&
              item.mainActivities &&
              item.position &&
              item.countryId &&
              item.percentageOfOwnership,
          )
          ?.map(async (item) => {
            const response = await createAffilatedCompany({
              id: baseInformation?.questionnaire?.id as string,
              body: {
                name: item.name,
                registrationNumber: item.registrationNumber,
                mainActivities: item.mainActivities,
                position: item.position,
                countryId: item.countryId,
                percentageOfOwnership: Number(item.percentageOfOwnership),
              },
            });

            return response;
          }),
      );
    }

    if (affilatedCompaniesForForUpdate?.length) {
      await Promise.all(
        affilatedCompaniesForForUpdate?.map(async (item) => {
          const response = await updateAffilatedCompany({
            id: item.id as string,
            body: {
              name: item.name,
              registrationNumber: item.registrationNumber,
              mainActivities: item.mainActivities,
              position: item.position,
              countryId: item.countryId,
              percentageOfOwnership: Number(item.percentageOfOwnership),
            },
          });

          return response;
        }),
      );
    }
  };

  const onSaveCRSAndFatca = async () => {
    const crsAndFatcaForCreation: CRSAndFatcaFormValues[] = [];
    const crsAndFatcaForForUpdate: CRSAndFatcaFormValues[] = [];

    crsAndFatcaValues?.forEach((item) => {
      if (item.id?.includes('crs-and-fatca')) {
        crsAndFatcaForCreation.push(item);
      } else {
        const currentcrsAndFatca = crsAndFatca?.find(
          (value) => value.id === item.id,
        );

        if (currentcrsAndFatca) {
          if (
            !isEqual(item, {
              id: currentcrsAndFatca.id,
              residenceCountryId: currentcrsAndFatca?.residenceCountryId,
              taxIdentificationNumber:
                currentcrsAndFatca?.taxIdentificationNumber,
              noTinReason: currentcrsAndFatca?.noTinReason,
            })
          ) {
            crsAndFatcaForForUpdate.push(item);
          }
        }
      }
    });

    if (crsAndFatcaForCreation?.length) {
      await Promise.all(
        crsAndFatcaForCreation
          ?.filter((item) => item.residenceCountryId)
          ?.map(async (item) => {
            let response;

            if (baseInformation?.type === OrganizationType.INDIVIDUAL) {
              response = await createCrsAndFatca({
                id: baseInformation?.questionnaire?.id as string,
                body: {
                  residenceCountryId: item.residenceCountryId,
                  taxIdentificationNumber: item.taxIdentificationNumber,
                  noTinReason: item.noTinReason,
                },
              });
            } else if (
              baseInformation?.type === OrganizationType.COMPANY_OFFICIAL
            ) {
              response = await createCOFCrsAndFatca({
                id: baseInformation?.questionnaire?.id as string,
                body: {
                  residenceCountryId: item.residenceCountryId,
                  taxIdentificationNumber: item.taxIdentificationNumber,
                  noTinReason: item.noTinReason,
                },
              });
            }

            return response;
          }),
      );
    }

    if (crsAndFatcaForForUpdate?.length) {
      await Promise.all(
        crsAndFatcaForForUpdate?.map(async (item) => {
          let response;

          if (baseInformation?.type === OrganizationType.INDIVIDUAL) {
            response = await updateCrsAndFatca({
              id: item.id as string,
              body: {
                residenceCountryId: item.residenceCountryId,
                taxIdentificationNumber: item.taxIdentificationNumber,
                noTinReason: item.noTinReason,
              },
            });
          } else if (
            baseInformation?.type === OrganizationType.COMPANY_OFFICIAL
          ) {
            response = await updateCOFCrsAndFatca({
              id: item.id as string,
              body: {
                residenceCountryId: item.residenceCountryId,
                taxIdentificationNumber: item.taxIdentificationNumber,
                noTinReason: item.noTinReason,
              },
            });
          }

          return response;
        }),
      );
    }
  };

  const onDeleteAffilatedCompany = async (id: string) => {
    if (id.includes('affilated-company')) {
      setAffilatedCompaniesValues(
        affilatedCompaniesValues?.filter((item) => item.id !== id),
      );
    } else {
      setSelectedAffilatedCompanyId(id);

      await deleteAffilatedCompany(id);
    }
  };

  const onDeleteAccountCurrencies = async (id: string) => {
    if (id.includes('accounts-currency')) {
      setAccountsCurrenciesValues(
        accountsCurrenciesValues?.filter((item) => item.id !== id),
      );

      const currentAccountsCurrency = accountsCurrenciesValues?.find(
        (item) => item.id === id,
      );

      if (currentAccountsCurrency) {
        const currentAccountsFlows = accountsFlowsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        const currentExpectedFundsOrigins = expectedFundsOriginsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        if (currentAccountsFlows?.length) {
          setAccountsFlowsValues(
            accountsFlowsValues?.filter((item) =>
              currentAccountsFlows.every((flow) => item.id !== flow.id),
            ),
          );
        }

        if (currentExpectedFundsOrigins?.length) {
          setExpectedFundsOriginsValues(
            expectedFundsOriginsValues?.filter((item) =>
              currentExpectedFundsOrigins.every(
                (expectedFund) => item.id !== expectedFund.id,
              ),
            ),
          );
        }
      }
    } else {
      setSelectedAccountsCurrencyId(id);

      const currentAccountsCurrency = accountsCurrenciesValues?.find(
        (item) => item.id === id,
      );

      if (currentAccountsCurrency) {
        const currentAccountsFlows = accountsFlowsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        const currentExpectedFundsOrigins = expectedFundsOriginsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        if (currentAccountsFlows?.length) {
          await Promise.all(
            currentAccountsFlows
              ?.filter((item) => !item.id?.includes('accounts-flow'))
              ?.map(async (item) => {
                const response = await deleteAccountsFlows(item.id as string);

                return response;
              }),
          );

          if (
            currentAccountsFlows?.filter((item) =>
              item.id?.includes('accounts-flow'),
            ).length
          ) {
            const notCreatedAccountsFlow = currentAccountsFlows?.filter(
              (item) => item.id?.includes('accounts-flow'),
            );

            setAccountsFlowsValues(
              accountsFlowsValues?.filter((item) =>
                notCreatedAccountsFlow.every((flow) => item.id !== flow.id),
              ),
            );
          }
        }

        if (currentExpectedFundsOrigins?.length) {
          await Promise.all(
            currentExpectedFundsOrigins
              ?.filter((item) => !item.id?.includes('expected-funds-origins'))
              ?.map(async (item) => {
                const response = await deleteExpectedFundsOrigins(
                  item.id as string,
                );

                return response;
              }),
          );

          if (
            currentExpectedFundsOrigins?.filter((item) =>
              item.id?.includes('expected-funds-origins'),
            )?.length
          ) {
            const notCreatedExpectedFundsOrigins =
              currentExpectedFundsOrigins?.filter((item) =>
                item.id?.includes('expected-funds-origins'),
              );

            setExpectedFundsOriginsValues(
              expectedFundsOriginsValues?.filter((item) =>
                notCreatedExpectedFundsOrigins.every(
                  (expectedFundsOrigin) => item.id !== expectedFundsOrigin.id,
                ),
              ),
            );
          }
        }
      }

      await deleteAccountsCurrencies(id);
    }
  };

  const onDeleteExpectedFundsOrigins = async (id: string) => {
    if (id.includes('expected-funds-origins')) {
      setExpectedFundsOriginsValues(
        expectedFundsOriginsValues?.filter((item) => item.id !== id),
      );
    } else {
      setSelectedFundsOriginsId(id);

      await deleteExpectedFundsOrigins(id);
    }
  };

  const onDeleteCRSAndFatca = async (id: string) => {
    if (id.includes('crs-and-fatca')) {
      setCrsAndFatcaValues(crsAndFatcaValues?.filter((item) => item.id !== id));
    } else {
      setSelectedCrsAndFatcaId(id);

      if (baseInformation?.type === OrganizationType.INDIVIDUAL) {
        await deleteCrsAndFatca(id);
      } else if (baseInformation?.type === OrganizationType.COMPANY_OFFICIAL) {
        await deleteCOFCrsAndFatca(id);
      }
    }
  };

  const onResetQuestionnaire = () => {
    reset(defaultQuestionnaire);
    organizationReset(defaultOrganizationValues);

    if (accountsCurrencies) {
      setAccountsCurrenciesValues(
        accountsCurrencies?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          accountOpeningPurpose: item.accountOpeningPurpose,
          plannedTransactionNature: item.plannedTransactionNature,
        })),
      );
    }

    if (accountsFlows) {
      setAccountsFlowsValues(
        accountsFlows?.map((item) => ({
          id: item.id,
          transactionType: item.transactionType,
          currencyId: item.currencyId,
          expectedAnnualCreditTurnover:
            item.expectedAnnualCreditTurnover.toString(),
          maxTransferAmountPerTransaction:
            item.maxTransferAmountPerTransaction.toString(),
          transactionFrequency: item.transactionFrequency.toString(),
          transactionFrequencyPeriodMonths:
            item.transactionFrequencyPeriodMonths.toString(),
        })),
      );
    }

    if (expectedFundsOrigins) {
      setExpectedFundsOriginsValues(
        expectedFundsOrigins?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          mainCounterpartiesAndWebsites: item.mainCounterpartiesAndWebsites,
          countryOfIncorporationId: item.countryOfIncorporationId,
          counterpartyBankCountryIds: item.counterpartyBankCountryIds,
          activityCountryIds: item.activityCountryIds,
          mainActivities: item.mainActivities,
          transactionReason: item.transactionReason,
          transactionType: item.transactionType,
        })),
      );
    }
  };

  const onUpdateQuestionnaire = handleSubmit(async (values) => {
    let body: Partial<COFAndIndividualQuestionnarieFormValues> = {};

    if (
      currentApplicationScheme?.id === 'ed560559-8354-4634-b6dd-eb341dfb551a' ||
      currentApplicationScheme?.id === 'a87db4b4-3c78-4564-bba1-c16a19553159'
    ) {
      const questionnarieBody = getIndividualRegulatedCompanyQuestionnaireBody({
        questionnarieValues: values as COFAndIndividualQuestionnarieFormValues,
      });

      body = {
        ...body,
        ...questionnarieBody,
      };

      if (baseInformation?.type === OrganizationType.INDIVIDUAL) {
        onSaveAccountsCurrencies();
        onSaveAccountsFlows();
        onSaveExpectedFundsOrigins();
      }

      if (baseInformation?.type === OrganizationType.COMPANY_OFFICIAL) {
        onSaveAffilatedCompanies();
      }

      onSaveCRSAndFatca();
    }

    if (!isEqual(defaultQuestionnaire, questionnaireFields)) {
      const response = (await updateQuestionnaire({
        id: customerId as string,
        body: body as Questionnarie,
      })) as { error?: ResponseError };

      if (response?.error) {
        responseErrorsResolver({
          errors: response?.error,
          setError,
        });
      }
    }

    if (!isEqual(defaultOrganizationValues, organizationFields)) {
      const organizationValues = getValues();

      const organizationResponse = (await updateIndividualInformation({
        id: baseInformation?.id as string,
        body: {
          ...organizationValues,
          isUSResident: Boolean(organizationValues?.isUSResident === 'yes'),
        } as BaseInfoFormValues,
      })) as { error?: ResponseError };

      if (organizationResponse?.error) {
        responseErrorsResolver({
          errors: organizationResponse?.error,
          setError: organizationSetError,
        });
      }
    }
  });

  const getCustomerAmlChangeLogsAction = async (cursor?: string) => {
    await getCustomerAmlScoresHistory({
      id: customerId as string,
      query: `?take=40${cursor ? `&cursor=${cursor}` : ''}`,
    });
  };

  const createIndividualManualRiskScoreAction = async () => {
    setManualRiskScoreError('');

    const response = (await createIndividualManualRiskScore({
      id: customerId as string,
      totalRiskScore,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setManualRiskScoreError,
      });
    }
  };

  const getIndividualOngoingWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    const response = await getIndividualOngoingWarningById({
      orgId: customerId as string,
      warningId,
      id,
    });

    if (
      !blacklistData &&
      !blacklistItem &&
      !blacklistDataVisible &&
      !blacklistItemVisible &&
      response
    ) {
      if (response?.data?.data?.blacklistDoc) {
        setBlacklistData(response?.data?.data?.blacklistDoc);
        setBlacklistDataVisible(true);
      } else if (response?.data?.data?.blacklistItem) {
        setBlacklistDataItem(
          formatBlacklistItem(response?.data?.data?.blacklistItem),
        );
        setBlacklistItemVisible(true);
      }
    }
  };

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=ORGANIZATION&entityId=${customerId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [customerId],
  );

  const handleCloseAmlChangeLogsPanel = () => {
    setRiskScoreChangeLogsSidepanelVisible(false);
    dispatch(clearAmlChangeLogs());
  };

  const getOngoingMonitoringPdfAction = async () => {
    const body: {
      createdDateFrom?: string;
      createdDateTo?: string;
    } = {};

    if (exportDate && exportDate.length) {
      body.createdDateFrom = new Date(exportDate[0]).toISOString();

      if (exportDate.length > 1) {
        body.createdDateTo = new Date(exportDate[1])?.toISOString();
      }
    }

    await getOngoingMonitoringPdf({
      id: customerId as string,
      body,
    });
  };

  const getIndividualWarningsAction = async ({
    warningGroupId,
    cursor,
  }: {
    warningGroupId: string;
    cursor: string;
  }) => {
    setSelectedWarningGroup(warningGroupId);

    await getIndividualWarning({
      id: customerId as string,
      warningGroupId,
      cursor,
    });
  };

  const onUpdateRange = (ranges: Record<string, string>) => {
    const updatedRanges: CustomerRanges = {};

    if (
      ranges.monthlyMaxValueIncomingPaymentRangeId !==
      baseInformation?.monthlyMaxValueIncomingPaymentRangeId
    ) {
      updatedRanges.monthlyMaxValueIncomingPaymentRangeId =
        ranges.monthlyMaxValueIncomingPaymentRangeId;
    }

    if (
      ranges.monthlyMaxValueOutgoingPaymentRangeId !==
      baseInformation?.monthlyMaxValueOutgoingPaymentRangeId
    ) {
      updatedRanges.monthlyMaxValueOutgoingPaymentRangeId =
        ranges.monthlyMaxValueOutgoingPaymentRangeId;
    }

    if (
      ranges.monthlyNumberIncomingTransactionsRangeId !==
      baseInformation?.monthlyNumberIncomingTransactionsRangeId
    ) {
      updatedRanges.monthlyNumberIncomingTransactionsRangeId =
        ranges.monthlyNumberIncomingTransactionsRangeId;
    }

    if (
      ranges.monthlyNumberOutgoingTransactionsRangeId !==
      baseInformation?.monthlyNumberOutgoingTransactionsRangeId
    ) {
      updatedRanges.monthlyNumberOutgoingTransactionsRangeId =
        ranges.monthlyNumberOutgoingTransactionsRangeId;
    }

    if (
      ranges.monthlyTurnoverRangeId !== baseInformation?.monthlyTurnoverRangeId
    ) {
      updatedRanges.monthlyTurnoverRangeId = ranges.monthlyTurnoverRangeId;
    }

    if (
      ranges.monthlyValueIncomingTransactionsRangeId !==
      baseInformation?.monthlyValueIncomingTransactionsRangeId
    ) {
      updatedRanges.monthlyValueIncomingTransactionsRangeId =
        ranges.monthlyValueIncomingTransactionsRangeId;
    }

    if (
      ranges.monthlyValueOutgoingTransactionsRangeId !==
      baseInformation?.monthlyValueOutgoingTransactionsRangeId
    ) {
      updatedRanges.monthlyValueOutgoingTransactionsRangeId =
        ranges.monthlyValueOutgoingTransactionsRangeId;
    }

    if (Object.keys(updatedRanges)?.length > 0) {
      updateIndividualRanges({
        id: customerId as string,
        body: updatedRanges,
      });
    }
  };

  const onSaveChanges = () => {
    onUpdateRange({
      monthlyMaxValueIncomingPaymentRangeId:
        cddFormValues?.monthlyMaxValueIncomingPaymentRangeId as string,
      monthlyMaxValueOutgoingPaymentRangeId:
        cddFormValues?.monthlyMaxValueOutgoingPaymentRangeId as string,
      monthlyNumberIncomingTransactionsRangeId:
        cddFormValues?.monthlyNumberIncomingTransactionsRangeId as string,
      monthlyNumberOutgoingTransactionsRangeId:
        cddFormValues?.monthlyNumberOutgoingTransactionsRangeId as string,
      monthlyTurnoverRangeId: cddFormValues?.monthlyTurnoverRangeId as string,
      monthlyValueIncomingTransactionsRangeId:
        cddFormValues?.monthlyValueIncomingTransactionsRangeId as string,
      monthlyValueOutgoingTransactionsRangeId:
        cddFormValues?.monthlyValueOutgoingTransactionsRangeId as string,
    });
  };

  const batchApplyIndividualOngoingWarningAction = async ({
    id,
    action,
    comment,
    matchStatus,
  }: {
    id: string;
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const response = (await batchApplyIndividualOngoingWarning({
      orgId: customerId as string,
      body: {
        warningGroupId: id,
        action,
        employeeId: currentEmployee?.id as string,
        comment,
        matchStatus,
      },
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const onResetTransactionWarningAction = async (
    warningId: string,
    id: string,
  ) => {
    setSelectedWarningId(warningId);

    const response = (await resetIndividualTransactionWarning({
      orgId: customerId as string,
      id,
      warningId,
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement> | OnSelectChange,
  ) => {
    const { name, value } = e.target;

    const nextCddFormValues = { ...cddFormValues };

    // @ts-ignore
    nextCddFormValues[name] = value;

    setCddFormValues(nextCddFormValues);
  };

  const applyIndividualOngoingWarningAction = async ({
    id,
    warningId,
    action,
    comment,
    matchStatus,
  }: {
    id: string;
    warningId: string;
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const response = (await applyIndividualOngoingWarning({
      orgId: customerId as string,
      id,
      warningId,
      body: {
        action,
        comment,
        employeeId: currentEmployee?.id as string,
        matchStatus,
      },
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const formattedData = useMemo(() => {
    const ongoingMonitoringList = ongoingMonitoring?.slice(0, 10);

    const data = formatOngoingMonitroing(
      ongoingMonitoringList as OngoingMonitring[],
    );

    return data;
  }, [ongoingMonitoring]);

  const onOngoingRowClick = (id: string) => {
    setOngoingItemPanelVisible(true);

    getIndividualOngoingMonitoringItem({ orgId: customerId as string, id });
  };

  const onUpdate = useCallback(() => {
    createIndividualScore(customerId as string);
  }, [customerId]);

  const getIndividualLasCalculaingScoreAction = useCallback(
    async (amlUpdateId: string) => {
      const { data } = await getIndividualAmlUpdatingItem({
        orgId: customerId as string,
        id: amlUpdateId,
      });

      if (
        data?.data.status === CustomerScoreUpdateStatus.FAIL ||
        data?.data.status === CustomerScoreUpdateStatus.SUCCESS
      ) {
        clearInterval(timeInterval);
      }

      if (data?.data.status === CustomerScoreUpdateStatus.SUCCESS) {
        getIndividualScore(customerId as string);
      }
    },
    [],
  );

  const getIndividualScoreAction = useCallback(async () => {
    (await getIndividualScore(customerId as string)) as {
      data: { data?: CustomerScore };
      error?: ResponseError;
    };
  }, [customerId]);

  useEffect(() => {
    if (baseInformation) {
      const body: CddFormValues = {
        monthlyMaxValueIncomingPaymentRangeId:
          baseInformation?.monthlyMaxValueIncomingPaymentRangeId,
        monthlyMaxValueOutgoingPaymentRangeId:
          baseInformation?.monthlyMaxValueOutgoingPaymentRangeId,
        monthlyNumberIncomingTransactionsRangeId:
          baseInformation?.monthlyNumberIncomingTransactionsRangeId,
        monthlyNumberOutgoingTransactionsRangeId:
          baseInformation?.monthlyNumberOutgoingTransactionsRangeId,
        monthlyTurnoverRangeId: baseInformation?.monthlyTurnoverRangeId,
        monthlyValueIncomingTransactionsRangeId:
          baseInformation?.monthlyValueIncomingTransactionsRangeId,
        monthlyValueOutgoingTransactionsRangeId:
          baseInformation?.monthlyValueOutgoingTransactionsRangeId,
      };

      setDefaultCddValues(body);
      setCddFormValues(body);
    }
  }, [baseInformation]);

  useEffect(() => {
    if (
      baseInformation &&
      baseInformation?.clientScheme &&
      applicationSchemes
    ) {
      const currentScheme = applicationSchemes?.find(
        (item) => item.name === baseInformation?.clientScheme?.name,
      );

      if (currentScheme) {
        setCurrentApplicationScheme(currentScheme);
      }
    }
  }, [baseInformation, applicationSchemes]);

  useEffect(() => {
    if (customerId && match) {
      navigate(
        generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_AML_TRANSACTIONS, {
          customerId,
        }),
      );
    }
  }, [customerId, match]);

  useEffect(() => {
    if (paginationIsSuccess) {
      setSelectedWarningGroup('');
    }
  }, [paginationIsSuccess]);

  useEffect(() => {
    if (search) {
      const parsed = queryString.parse(search);

      if (parsed.ongoingMonitoringItemId) {
        setOngoingItemPanelVisible(true);

        onOngoingRowClick(parsed.ongoingMonitoringItemId as string);
      }
    }
  }, [search]);

  useEffect(() => {
    if (!amlGeneralInfo.values) {
      dispatch(getAmlGeneralInfo());
    }
  }, [dispatch, amlGeneralInfo.values]);

  useEffect(() => {
    if (createRiskScoreIsSuccess) {
      setManualRiskScoreError('');
      setTotalRiskScore(0);
      setTotalRiskScoreLevel('');
      setManualScoreSidepanelVisible(false);
      getIndividualScoreAction();
    }
  }, [createRiskScoreIsSuccess]);

  useEffect(() => {
    if (
      amlUpdateStatus &&
      amlUpdateStatus === CustomerScoreUpdateStatus.SUCCESS &&
      activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_LIST,
      )
    ) {
      getIndividualLastCalculaingScore(customerId as string);
    }
  }, [amlUpdateStatus, activePermissions]);

  useEffect(() => {
    if (
      changeLogsPanelIsOpen &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [changeLogsPanelIsOpen]);

  useEffect(() => {
    if (customerId) {
      if (
        activePermissions?.includes(
          PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_LIST,
        )
      ) {
        getIndividualScoreAction();
      }

      if (
        baseInformation?.type !== OrganizationType.COMPANY_OFFICIAL &&
        activePermissions?.includes(
          PermissionEnum.INDIVIDUAL_CUSTOMERS_AML_LIST,
        )
      ) {
        getIndividualOngoingMonitoring({ id: customerId });
      }
    }
  }, [customerId, activePermissions]);

  useEffect(() => {
    if (
      amlUpdateStatus &&
      activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_LIST,
      )
    ) {
      const amlUpdateId = localStorage.getItem('amlUpdateId');

      if (amlUpdateId) {
        timeInterval = setInterval(() => {
          getIndividualLasCalculaingScoreAction(amlUpdateId);
        }, 15000);
      }
    }
  }, [amlUpdateStatus, activePermissions]);

  useEffect(() => {
    if (isSuccess && aml) {
      setSuccessMessageVisible(true);

      setTimeout(() => {
        setSuccessMessageVisible(false);
      }, 6000);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (aml && riskScoreRanges && manualScoreSidepanelVisible) {
      setTotalRiskScore(aml?.totalRiskScore);

      setTotalRiskScoreLevel(
        riskScoreRanges?.find(
          (item) =>
            aml?.totalRiskScore >= item.rangeStart &&
            aml?.totalRiskScore <= item.rangeEnd,
        )?.id as string,
      );
    }
  }, [aml, riskScoreRanges, manualScoreSidepanelVisible]);

  useEffect(() => {
    if (ongoingMonitoringPdfIsSuccess) {
      setExportDate([]);
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'individual-client-outgoing-monitoring',
        }),
      );
      setExportPdfPanelVisible(false);
    }
  }, [ongoingMonitoringPdfIsSuccess]);

  useEffect(() => {
    let title =
      baseInformation?.firstName || baseInformation?.lastName
        ? `${baseInformation?.firstName} ${baseInformation?.lastName || ''}: AML & KYC`
        : 'AML & KYC';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseInformation?.firstName, baseInformation?.lastName]);

  let amlContent;

  useEffect(() => {
    if (
      prevClientActivitiy &&
      prevClientActivitiy === employmentActivity?.value &&
      organizationFields.clientActivityId !== employmentActivity?.value
    ) {
      organizationSetValue('nameOfEmployer', '');
      organizationSetValue('countryOfEmployerId', '');
      organizationSetValue('employeePosition', '');
      setValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy === businessOwnerActivity?.value &&
      organizationFields.clientActivityId !== businessOwnerActivity?.value
    ) {
      organizationSetValue('countryOfEmployerId', '');
      setValue('nameOfCompany', '');
      setValue('ownership', '');
      setValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy ===
        clientsActivities?.find((item) => item.name === 'Self-employed')
          ?.value &&
      organizationFields.clientActivityId !==
        clientsActivities?.find((item) => item.name === 'Self-employed')?.value
    ) {
      setValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy === otherActivity?.value &&
      organizationFields.clientActivityId !== otherActivity?.value
    ) {
      organizationSetValue('typeOfActivityDescription', '');
    }
  }, [organizationFields.clientActivityId, prevClientActivitiy]);

  useEffect(() => {
    if (accountsCurrencies) {
      setAccountsCurrenciesValues(
        accountsCurrencies?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          accountOpeningPurpose: item.accountOpeningPurpose,
          plannedTransactionNature: item.plannedTransactionNature,
        })),
      );
    }
  }, [accountsCurrencies]);

  useEffect(() => {
    if (!applicationSchemes) {
      getApplicationsSchemes();
    }
  }, [applicationSchemes]);

  useEffect(() => {
    if (accountsFlows && accountsCurrencies) {
      const nextAccountsFlows = accountsFlows?.map((item) => ({
        id: item.id,
        transactionType: item.transactionType,
        currencyId: item.currencyId,
        expectedAnnualCreditTurnover:
          item.expectedAnnualCreditTurnover.toString(),
        maxTransferAmountPerTransaction:
          item.maxTransferAmountPerTransaction.toString(),
        transactionFrequency: item.transactionFrequency.toString(),
        transactionFrequencyPeriodMonths:
          item.transactionFrequencyPeriodMonths.toString(),
      }));

      const notCreatedCurrencies: AccountsCurrencies[] = [];

      accountsCurrencies?.forEach((item) => {
        if (
          !nextAccountsFlows?.find(
            (flow) => flow.currencyId === item.currencyId,
          )
        ) {
          notCreatedCurrencies.push(item);
        }
      });

      if (notCreatedCurrencies?.length) {
        notCreatedCurrencies?.forEach((item) => {
          nextAccountsFlows.push(
            {
              id: `accounts-flow-${uuid()}`,
              transactionType: 'INCOMING',
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: '',
              maxTransferAmountPerTransaction: '',
              transactionFrequency: '',
              transactionFrequencyPeriodMonths: '',
            },
            {
              id: `accounts-flow-${uuid()}`,
              transactionType: 'OUTGOING',
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: '',
              maxTransferAmountPerTransaction: '',
              transactionFrequency: '',
              transactionFrequencyPeriodMonths: '',
            },
          );
        });
      }

      setAccountsFlowsValues(nextAccountsFlows);
    }
  }, [accountsFlows, accountsCurrencies]);

  useEffect(() => {
    if (expectedFundsOrigins) {
      setExpectedFundsOriginsValues(
        expectedFundsOrigins?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          mainCounterpartiesAndWebsites: item.mainCounterpartiesAndWebsites,
          countryOfIncorporationId: item.countryOfIncorporationId,
          counterpartyBankCountryIds: item.counterpartyBankCountryIds,
          activityCountryIds: item.activityCountryIds,
          mainActivities: item.mainActivities,
          transactionReason: item.transactionReason,
          transactionType: item.transactionType,
        })),
      );
    }
  }, [expectedFundsOrigins]);

  useEffect(() => {
    if (affiliatedCompanies) {
      setAffilatedCompaniesValues(
        affiliatedCompanies?.map((item) => ({
          id: item.id,
          name: item.name,
          registrationNumber: item.registrationNumber,
          mainActivities: item.mainActivities,
          position: item.position,
          countryId: item.countryId,
          percentageOfOwnership: item.percentageOfOwnership?.toString(),
        })),
      );
    }
  }, [affiliatedCompanies]);

  useEffect(() => {
    if (deleteAffilatedCompanyIsSuccess) {
      setSelectedAffilatedCompanyId('');
    }
  }, [deleteAffilatedCompanyIsSuccess]);

  useEffect(() => {
    if (crsAndFatca) {
      setCrsAndFatcaValues(
        crsAndFatca?.map((item) => ({
          id: item.id,
          residenceCountryId: item?.residenceCountryId,
          taxIdentificationNumber: item?.taxIdentificationNumber || '',
          noTinReason: item?.noTinReason || '',
        })),
      );
    }
  }, [crsAndFatca]);

  useEffect(() => {
    if (deleteAccountsCurrenciesIsSuccess) {
      setSelectedAccountsCurrencyId('');
    }
  }, [deleteAccountsCurrenciesIsSuccess]);

  useEffect(() => {
    if (deleteExpectedFundsOriginsIsSuccess) {
      setSelectedFundsOriginsId('');
    }
  }, [deleteExpectedFundsOriginsIsSuccess]);

  useEffect(() => {
    if (deleteCrsAndFatcaIsSuccess || deleteCofCrsAndFatcaIsSuccess) {
      setSelectedCrsAndFatcaId('');
    }
  }, [deleteCrsAndFatcaIsSuccess, deleteCofCrsAndFatcaIsSuccess]);

  useEffect(() => {
    if (
      baseInformation &&
      baseInformation?.questionnaire &&
      currentApplicationScheme &&
      Boolean(
        currentApplicationScheme?.id === 'ed560559-8354-4634-b6dd-eb341dfb551a',
      )
    ) {
      if (!accountsCurrencies) {
        getAccountsCurrencies(baseInformation?.questionnaire?.id);
      }

      if (!accountsFlows) {
        getAccountsFlows(baseInformation?.questionnaire?.id);
      }

      if (!expectedFundsOrigins) {
        getExpectedFundsOrigins(baseInformation?.questionnaire?.id);
      }

      if (!crsAndFatca) {
        getCrsAndFatca(baseInformation?.questionnaire?.id);
      }
    }
  }, [
    baseInformation,
    currentApplicationScheme,
    accountsCurrencies,
    accountsFlows,
    crsAndFatca,
  ]);

  useEffect(() => {
    if (
      baseInformation &&
      baseInformation?.questionnaire &&
      currentApplicationScheme &&
      Boolean(
        currentApplicationScheme?.id === 'a87db4b4-3c78-4564-bba1-c16a19553159',
      )
    ) {
      if (!crsAndFatca) {
        getCOFCrsAndFatca(baseInformation?.questionnaire?.id);
      }

      if (!affiliatedCompanies) {
        getAffilatedCompanies(baseInformation?.questionnaire?.id);
      }
    }
  }, [
    baseInformation,
    currentApplicationScheme,
    affiliatedCompanies,
    crsAndFatca,
  ]);

  useEffect(() => {
    if (
      currentApplicationScheme &&
      (currentApplicationScheme?.id ===
        'ed560559-8354-4634-b6dd-eb341dfb551a' ||
        currentApplicationScheme?.id ===
          'a87db4b4-3c78-4564-bba1-c16a19553159') &&
      baseInformation
    ) {
      const body = {
        birthCountryId: baseInformation?.birthCountryId || '',
        placeOfBirth: baseInformation?.placeOfBirth || '',
        nameOfEmployer: baseInformation?.nameOfEmployer || '',
        employeePosition: baseInformation?.employeePosition || '',
        countryOfEmployerId: baseInformation?.countryOfEmployerId || '',
        correspondenceAddress1: baseInformation?.correspondenceAddress1 || '',
        correspondenceAddress2: baseInformation?.correspondenceAddress2 || '',
        correspondenceAddressSameAsRegisteredAddress:
          baseInformation?.correspondenceAddressSameAsRegisteredAddress,
        correspondenceCity: baseInformation?.correspondenceCity || '',
        correspondenceCountryId: baseInformation?.correspondenceCountryId || '',
        correspondencePostalCode:
          baseInformation?.correspondencePostalCode || '',
        typeOfActivityDescription:
          baseInformation?.typeOfActivityDescription || '',
        clientActivityId: baseInformation?.clientActivityId || '',
        isUSResident:
          currentApplicationScheme?.id ===
            'ed560559-8354-4634-b6dd-eb341dfb551a' &&
          baseInformation?.isUSResident?.toString() &&
          baseInformation?.isUSResident?.toString() !== 'null'
            ? baseInformation?.isUSResident === false
              ? 'no'
              : 'yes'
            : '',
      };

      organizationReset(body);

      setDefaultOrganizationValues(body);
    }
  }, [baseInformation, currentApplicationScheme]);

  useEffect(() => {
    if (
      (activePermissions?.includes(PermissionEnum.DICTIONARIES_ITEMS_LIST) &&
        currentApplicationScheme &&
        Boolean(
          currentApplicationScheme?.id ===
          'ed560559-8354-4634-b6dd-eb341dfb551a',
        )) ||
      Boolean(
        currentApplicationScheme?.id === 'a87db4b4-3c78-4564-bba1-c16a19553159',
      )
    ) {
      if (!sourceOfIncome) {
        getCustomersSourceOfIncome();
      }

      if (!sourceOfWealth) {
        getCustomersSourceOfWealth();
      }

      if (!sourceOfWealthForIndividual) {
        getCustomersSourceOfWealthForIndividua();
      }

      if (!customerSourceOfFunds) {
        getCustomersSourceOfFundsDictionary();
      }
    }
  }, [currentApplicationScheme, activePermissions]);

  useEffect(() => {
    if (baseInformation?.questionnaire) {
      const body = {
        ...baseInformation?.questionnaire,
        criminalConvictedOfAny:
          baseInformation?.questionnaire?.criminalConvictedOfAny?.toString() &&
          baseInformation?.questionnaire?.criminalConvictedOfAny?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.criminalConvictedOfAny === false
              ? 'no'
              : 'yes'
            : '',
        criminalReceivedACautionForAny:
          baseInformation?.questionnaire?.criminalReceivedACautionForAny?.toString() &&
          baseInformation?.questionnaire?.criminalReceivedACautionForAny?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.criminalReceivedACautionForAny ===
              false
              ? 'no'
              : 'yes'
            : '',
        criminalArrestedOrChargedWithOrBeenInvolved:
          baseInformation?.questionnaire?.criminalArrestedOrChargedWithOrBeenInvolved?.toString() &&
          baseInformation?.questionnaire?.criminalArrestedOrChargedWithOrBeenInvolved?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.criminalArrestedOrChargedWithOrBeenInvolved === false
              ? 'no'
              : 'yes'
            : '',
        criminalSubjectToAnyCurrentCriminalInvestigation:
          baseInformation?.questionnaire?.criminalSubjectToAnyCurrentCriminalInvestigation?.toString() &&
          baseInformation?.questionnaire?.criminalSubjectToAnyCurrentCriminalInvestigation?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.criminalSubjectToAnyCurrentCriminalInvestigation === false
              ? 'no'
              : 'yes'
            : '',
        criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted:
          baseInformation?.questionnaire?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted?.toString() &&
          baseInformation?.questionnaire?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted ===
              false
              ? 'no'
              : 'yes'
            : '',
        criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered:
          baseInformation?.questionnaire.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered?.toString() &&
          baseInformation?.questionnaire.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered ===
              false
              ? 'no'
              : 'yes'
            : '',
        criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved:
          baseInformation?.questionnaire?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved?.toString() &&
          baseInformation?.questionnaire?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved ===
              false
              ? 'no'
              : 'yes'
            : '',
        civilFoundLiableForFraud:
          baseInformation?.questionnaire?.civilFoundLiableForFraud?.toString() &&
          baseInformation?.questionnaire?.civilFoundLiableForFraud?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.civilFoundLiableForFraud === false
              ? 'no'
              : 'yes'
            : '',
        civilSubjectToAJudgmentDebt:
          baseInformation?.questionnaire?.civilSubjectToAJudgmentDebt?.toString() &&
          baseInformation?.questionnaire?.civilSubjectToAJudgmentDebt?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.civilSubjectToAJudgmentDebt ===
              false
              ? 'no'
              : 'yes'
            : '',
        civilInvolvedInAnyCivilProceedings:
          baseInformation?.questionnaire?.civilInvolvedInAnyCivilProceedings?.toString() &&
          baseInformation?.questionnaire?.civilInvolvedInAnyCivilProceedings?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.civilInvolvedInAnyCivilProceedings === false
              ? 'no'
              : 'yes'
            : '',
        civilYouHeldAPositionOfInfluence:
          baseInformation?.questionnaire?.civilYouHeldAPositionOfInfluence?.toString() &&
          baseInformation?.questionnaire?.civilYouHeldAPositionOfInfluence?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.civilYouHeldAPositionOfInfluence === false
              ? 'no'
              : 'yes'
            : '',
        civilFiledForBankruptcy:
          baseInformation?.questionnaire?.civilFiledForBankruptcy?.toString() &&
          baseInformation?.questionnaire?.civilFiledForBankruptcy?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.civilFiledForBankruptcy === false
              ? 'no'
              : 'yes'
            : '',
        civilSubjectToABankruptcy:
          baseInformation?.questionnaire?.civilSubjectToABankruptcy?.toString() &&
          baseInformation?.questionnaire?.civilSubjectToABankruptcy?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.civilSubjectToABankruptcy ===
              false
              ? 'no'
              : 'yes'
            : '',
        businessAndEmploymentDismissedOrAskedTo:
          baseInformation?.questionnaire?.businessAndEmploymentDismissedOrAskedTo?.toString() &&
          baseInformation?.questionnaire?.businessAndEmploymentDismissedOrAskedTo?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.businessAndEmploymentDismissedOrAskedTo === false
              ? 'no'
              : 'yes'
            : '',
        businessAndEmploymentDisqualifiedFromActingAsADirector:
          baseInformation?.questionnaire?.businessAndEmploymentDisqualifiedFromActingAsADirector?.toString() &&
          baseInformation?.questionnaire?.businessAndEmploymentDisqualifiedFromActingAsADirector?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.businessAndEmploymentDisqualifiedFromActingAsADirector ===
              false
              ? 'no'
              : 'yes'
            : '',
        businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings:
          baseInformation?.questionnaire?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings?.toString() &&
          baseInformation?.questionnaire?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings ===
              false
              ? 'no'
              : 'yes'
            : '',
        regulatoryAnAuthorizationFromARegulatoryBodyRefused:
          baseInformation?.questionnaire?.regulatoryAnAuthorizationFromARegulatoryBodyRefused?.toString() &&
          baseInformation?.questionnaire?.regulatoryAnAuthorizationFromARegulatoryBodyRefused?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.regulatoryAnAuthorizationFromARegulatoryBodyRefused === false
              ? 'no'
              : 'yes'
            : '',
        regulatoryCensured:
          baseInformation?.questionnaire?.regulatoryCensured?.toString() &&
          baseInformation?.questionnaire?.regulatoryCensured?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.regulatoryCensured === false
              ? 'no'
              : 'yes'
            : '',
        regulatoryInvolvedInAnyActivitiesRequiringAuthorization:
          baseInformation?.questionnaire?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization?.toString() &&
          baseInformation?.questionnaire?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization ===
              false
              ? 'no'
              : 'yes'
            : '',
        regulatorySubjectToAnyDisqualification:
          baseInformation?.questionnaire?.regulatorySubjectToAnyDisqualification?.toString() &&
          baseInformation?.questionnaire?.regulatorySubjectToAnyDisqualification?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.regulatorySubjectToAnyDisqualification === false
              ? 'no'
              : 'yes'
            : '',
        ownership: baseInformation?.questionnaire?.ownership?.toString() || '',
        belongsToGroup:
          baseInformation?.questionnaire?.belongsToGroup?.toString() &&
          baseInformation?.questionnaire?.belongsToGroup?.toString() !== 'null'
            ? baseInformation?.questionnaire?.belongsToGroup === false
              ? 'no'
              : 'yes'
            : '',
        estimatedTotalWealthEuro:
          baseInformation?.questionnaire?.estimatedTotalWealthEuro ||
          baseInformation?.questionnaire?.estimatedTotalWealthEuro?.toString() ===
            '0'
            ? baseInformation?.questionnaire?.estimatedTotalWealthEuro?.toString()
            : '',
        requiresSourcesOfIncomes:
          baseInformation?.questionnaire?.requiresSourcesOfIncomes?.toString() &&
          baseInformation?.questionnaire?.requiresSourcesOfIncomes?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.requiresSourcesOfIncomes === false
              ? 'no'
              : 'yes'
            : '',
        requiresSourceAndSizeOfWealth:
          baseInformation?.questionnaire?.requiresSourceAndSizeOfWealth?.toString() &&
          baseInformation?.questionnaire?.requiresSourceAndSizeOfWealth?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.requiresSourceAndSizeOfWealth ===
              false
              ? 'no'
              : 'yes'
            : '',
        requiresNetWorth:
          baseInformation?.questionnaire?.requiresNetWorth?.toString() &&
          baseInformation?.questionnaire?.requiresNetWorth?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.requiresNetWorth === false
              ? 'no'
              : 'yes'
            : '',
        isPepOrAssociate:
          baseInformation?.questionnaire?.isPepOrAssociate?.toString() &&
          baseInformation?.questionnaire?.isPepOrAssociate?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.isPepOrAssociate === false
              ? 'no'
              : 'yes'
            : '',
        hasAffiliatedCompanies:
          baseInformation?.questionnaire?.hasAffiliatedCompanies?.toString() &&
          baseInformation?.questionnaire?.hasAffiliatedCompanies?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.hasAffiliatedCompanies === false
              ? 'no'
              : 'yes'
            : '',
        abilityMoveGovernmentFunds:
          baseInformation?.questionnaire?.abilityMoveGovernmentFunds?.toString() &&
          baseInformation?.questionnaire?.abilityMoveGovernmentFunds?.toString() !==
            'null'
            ? baseInformation?.questionnaire?.abilityMoveGovernmentFunds ===
              false
              ? 'no'
              : 'yes'
            : '',
        isUSCitizen:
          baseInformation?.questionnaire?.isUSCitizen?.toString() &&
          baseInformation?.questionnaire?.isUSCitizen?.toString() !== 'null'
            ? baseInformation?.questionnaire?.isUSCitizen === false
              ? 'no'
              : 'yes'
            : '',
        hasAccountsInOtherFinancialInstitutions:
          baseInformation?.questionnaire?.hasAccountsInOtherFinancialInstitutions?.toString() &&
          baseInformation?.questionnaire?.hasAccountsInOtherFinancialInstitutions?.toString() !==
            'null'
            ? baseInformation?.questionnaire
                ?.hasAccountsInOtherFinancialInstitutions === false
              ? 'no'
              : 'yes'
            : '',
      };

      reset(body);

      setDefaultQuestionaire(body);
    }
  }, [baseInformation]);

  useEffect(() => {
    if (
      (showOngoing ||
        baseInformation?.type === OrganizationType.COMPANY_OFFICIAL) &&
      customerId &&
      activePermissions?.includes(PermissionEnum.INDIVIDUAL_CUSTOMERS_AML_LIST)
    ) {
      getIndividualOngoingMonitoring({ id: customerId, take: '40' });
    }
  }, [showOngoing, customerId, activePermissions]);

  let ongoingSidepanelContent = <div />;

  if (ongoingItemIsFetching || ongoingItemIsLoading) {
    ongoingSidepanelContent = <SidePanelLoader />;
  } else if (
    !ongoingItemIsFetching &&
    !ongoingItemIsLoading &&
    ongoingMonitoringItem
  ) {
    ongoingSidepanelContent = (
      <OngoingPanelContent
        warnings={ongoingMonitoringItem?.warnings.edges.map(
          (item) => item.node,
        )}
        warningsPagination={ongoingMonitoringItem?.warnings?.pageInfo}
        setOngoingItemPanelVisible={setOngoingItemPanelVisible}
        setBlacklistDataVisible={setBlacklistDataVisible}
        setBlacklistData={setBlacklistData}
        setBlacklistDataItem={setBlacklistDataItem}
        setBlacklistItemVisible={setBlacklistItemVisible}
        type={OrganizationType.INDIVIDUAL}
        blacklists={ongoingMonitoringItem?.warningsGroupsBlacklists?.edges?.map(
          (item) => item?.node?.blacklist?.name,
        )}
        warningGroupId={ongoingMonitoringItem.id}
        applyError={applyError}
        applyWarningInProgress={applyWarningInProgress}
        applyOngoingWarning={applyIndividualOngoingWarningAction}
        batchApplyWarningInProgress={batchApplyWarningInProgress}
        batchApplyOngoingWarning={batchApplyIndividualOngoingWarningAction}
        batchApplyWarningIsSuccess={batchApplyWarningIsSuccess}
        onResetTransactionWarningAction={onResetTransactionWarningAction}
        resetTransactionsWarningIsLoading={resetTransactionsWarningIsLoading}
        resetTransactionsWarningIsSuccess={resetTransactionsWarningIsSuccess}
        selectedWarningId={selectedWarningId}
        status={baseInformation?.status}
        selectedWarningGroup={selectedWarningGroup}
        getWarningsAction={getIndividualWarningsAction}
        paginationInProgress={paginationInProgress || paginationIsFetching}
        getOngoingWarningById={getIndividualOngoingWarningByIdAction}
        isWarningByIdLoading={isWarningByIdLoading}
        isWarningByIdFetching={isWarningByIdFetching}
      />
    );
  }

  if (
    showOngoing ||
    baseInformation?.type === OrganizationType.COMPANY_OFFICIAL
  ) {
    amlContent = (
      <>
        {baseInformation?.questionnaire &&
          Object.keys(baseInformation?.questionnaire)?.length > 0 && (
            <AdditionalInfo
              questionnaire={baseInformation?.questionnaire}
              control={
                (baseInformation?.type === OrganizationType.INDIVIDUAL &&
                  currentApplicationScheme?.id ===
                    'ed560559-8354-4634-b6dd-eb341dfb551a') ||
                (baseInformation?.type === OrganizationType.COMPANY_OFFICIAL &&
                  currentApplicationScheme?.id ===
                    'a87db4b4-3c78-4564-bba1-c16a19553159')
                  ? control
                  : undefined
              }
              setValue={setValue}
              currentApplicationScheme={currentApplicationScheme}
              questionnaireFields={questionnaireFields}
              isDirty={
                currentApplicationScheme?.id !==
                  'ed560559-8354-4634-b6dd-eb341dfb551a' &&
                currentApplicationScheme?.id !==
                  'a87db4b4-3c78-4564-bba1-c16a19553159'
                  ? !isEqual(defaultQuestionnaire, questionnaireFields) ||
                    !isEqual(defaultOrganizationValues, organizationFields)
                  : true
              }
              onResetQuestionnaire={onResetQuestionnaire}
              onUpdateQuestionnaire={onUpdateQuestionnaire}
              requestInProgress={
                questionnnaireRequestInProgress || informationIsUpdate
              }
              status={baseInformation?.status}
              errors={errors}
              clearErrors={clearErrors}
              countries={countriesList}
              baseCurrency={baseCurrency}
              accountsCurrenciesIsLoading={accountsCurrenciesIsLoading}
              accountsCurrenciesIsFetching={accountsCurrenciesIsFetching}
              accountsCurrenciesValues={accountsCurrenciesValues}
              setAccountsCurrenciesValues={setAccountsCurrenciesValues}
              currencies={currencies}
              onDeleteAccountCurrencies={onDeleteAccountCurrencies}
              selectedAccountsCurrencyId={selectedAccountsCurrencyId}
              deleteAccountsCurrenciesInProgress={
                deleteAccountsCurrenciesInProgress
              }
              setAccountsFlowsValues={setAccountsFlowsValues}
              accountsFlowsValues={accountsFlowsValues}
              accountsFlowsIsLoading={accountsFlowsIsLoading}
              accountsFlowsIsFetching={accountsFlowsIsFetching}
              currenciesList={currenciesList}
              expectedFundsOriginsIsLoading={expectedFundsOriginsIsLoading}
              expectedFundsOriginsIsFetching={expectedFundsOriginsIsFetching}
              deleteExpectedFundsOriginsInProgress={
                deleteExpectedFundsOriginsInProgress
              }
              onDeleteExpectedFundsOrigins={onDeleteExpectedFundsOrigins}
              selectedExpectedFundsOriginsId={selectedExpectedFundsOriginsId}
              expectedFundsOriginsValues={expectedFundsOriginsValues}
              setExpectedFundsOriginsValues={setExpectedFundsOriginsValues}
              createAccountsCurrenciesIsProgress={
                createAccountsCurrenciesIsProgress
              }
              updateAccountsCurrenciesInProgress={
                updateAccountsCurrenciesInProgress
              }
              createAccountsFlowsIsProgress={createAccountsFlowsIsProgress}
              updateAccountsFlowsInProgress={updateAccountsFlowsInProgress}
              createExpectedFundsOriginsInProgress={
                createExpectedFundsOriginsInProgress
              }
              updateExpectedFundsOriginsInProgress={
                updateExpectedFundsOriginsInProgress
              }
              sourceOfWealth={sourceOfWealth}
              sourceOfIncome={sourceOfIncome}
              sourceOfWealthForIndividual={sourceOfWealthForIndividual}
              customerSourceOfFunds={customerSourceOfFunds}
              selectedCrsAndFatcaId={selectedCrsAndFatcaId}
              crsAndFatcaIsLoading={
                crsAndFatcaIsLoading || cofCrsAndFatcaIsLoading
              }
              crsAndFatcaIsFetching={
                crsAndFatcaIsFetching || cofCrsAndFatcaIsFetching
              }
              createCrsAndFatcaIsProgress={
                createCrsAndFatcaIsProgress || createCofCrsAndFatcaIsProgress
              }
              updateCrsAndFatcaInProgress={
                updateCrsAndFatcaInProgress || updateCofCrsAndFatcaInProgress
              }
              deleteCrsAndFatcaInProgress={
                deleteCrsAndFatcaInProgress || deleteCofCrsAndFatcaInProgress
              }
              onDeleteCRSAndFatca={onDeleteCRSAndFatca}
              organizationControl={organizationControl}
              organizationErrors={organizationErrors}
              organizationClearErrors={organizationClearErrors}
              organizationFields={organizationFields}
              organizationSetValue={organizationSetValue}
              crsAndFatcaValues={crsAndFatcaValues}
              setCrsAndFatcaValues={setCrsAndFatcaValues}
              isCof={
                baseInformation?.type === OrganizationType.COMPANY_OFFICIAL
              }
              affilatedCompaniesValues={affilatedCompaniesValues}
              setAffilatedCompaniesValues={setAffilatedCompaniesValues}
              selectedAffilatedCompanyId={selectedAffilatedCompanyId}
              affilatedCompaniesIsLoading={affilatedCompaniesIsLoading}
              affilatedCompaniesIsFetching={affilatedCompaniesIsFetching}
              createAffilatedCompanyInProgress={
                createAffilatedCompanyInProgress
              }
              updateAffilatedCompanyInProgress={
                updateAffilatedCompanyInProgress
              }
              deleteAffilatedCompanyInProgress={
                deleteAffilatedCompanyInProgress
              }
              onDeleteAffilatedCompany={onDeleteAffilatedCompany}
            />
          )}
        <OngoingMonitoring
          setShowOngoing={setShowOngoing}
          onRowClick={onOngoingRowClick}
          isLoading={ongoingIsLoading || ongoingIsFetching}
          type={baseInformation?.type as OrganizationType}
          getOngoingMonitroing={({ id, take, cursor }) =>
            getIndividualOngoingMonitoring({
              id,
              take: take || '',
              cursor: cursor || '',
            })
          }
          setExportPdfPanelVisible={setExportPdfPanelVisible}
          exportAvailable={activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_AML_EXPORT_ACTION,
          )}
        />
      </>
    );
  } else {
    amlContent = (
      <>
        <AmlGeneralInfo
          isLoading={amlGeneralInfo.isLoading}
          baseInformation={baseInformation}
          nextReviewDate={nextReviewDate}
          auditLogAllowed={activePermissions?.includes(
            PermissionEnum.AUDIT_LOG_LIST,
          )}
          setChangeLogPanelIsOpen={setChangeLogPanelIsOpen}
        />
        {baseInformation?.questionnaire &&
          Object.keys(baseInformation?.questionnaire)?.length > 0 && (
            <AdditionalInfo
              questionnaire={baseInformation?.questionnaire}
              control={
                currentApplicationScheme?.id ===
                'ed560559-8354-4634-b6dd-eb341dfb551a'
                  ? control
                  : undefined
              }
              setValue={setValue}
              currentApplicationScheme={currentApplicationScheme}
              questionnaireFields={questionnaireFields}
              isDirty={
                currentApplicationScheme?.id !==
                'ed560559-8354-4634-b6dd-eb341dfb551a'
                  ? !isEqual(defaultQuestionnaire, questionnaireFields) ||
                    !isEqual(defaultOrganizationValues, organizationFields)
                  : true
              }
              onResetQuestionnaire={onResetQuestionnaire}
              onUpdateQuestionnaire={onUpdateQuestionnaire}
              requestInProgress={
                questionnnaireRequestInProgress || informationIsUpdate
              }
              status={baseInformation?.status}
              errors={errors}
              clearErrors={clearErrors}
              countries={countriesList}
              baseCurrency={baseCurrency}
              accountsCurrenciesIsLoading={accountsCurrenciesIsLoading}
              accountsCurrenciesIsFetching={accountsCurrenciesIsFetching}
              accountsCurrenciesValues={accountsCurrenciesValues}
              setAccountsCurrenciesValues={setAccountsCurrenciesValues}
              currencies={currencies}
              onDeleteAccountCurrencies={onDeleteAccountCurrencies}
              selectedAccountsCurrencyId={selectedAccountsCurrencyId}
              deleteAccountsCurrenciesInProgress={
                deleteAccountsCurrenciesInProgress
              }
              setAccountsFlowsValues={setAccountsFlowsValues}
              accountsFlowsValues={accountsFlowsValues}
              accountsFlowsIsLoading={accountsFlowsIsLoading}
              accountsFlowsIsFetching={accountsFlowsIsFetching}
              currenciesList={currenciesList}
              expectedFundsOriginsIsLoading={expectedFundsOriginsIsLoading}
              expectedFundsOriginsIsFetching={expectedFundsOriginsIsFetching}
              deleteExpectedFundsOriginsInProgress={
                deleteExpectedFundsOriginsInProgress
              }
              onDeleteExpectedFundsOrigins={onDeleteExpectedFundsOrigins}
              selectedExpectedFundsOriginsId={selectedExpectedFundsOriginsId}
              expectedFundsOriginsValues={expectedFundsOriginsValues}
              setExpectedFundsOriginsValues={setExpectedFundsOriginsValues}
              createAccountsCurrenciesIsProgress={
                createAccountsCurrenciesIsProgress
              }
              updateAccountsCurrenciesInProgress={
                updateAccountsCurrenciesInProgress
              }
              createAccountsFlowsIsProgress={createAccountsFlowsIsProgress}
              updateAccountsFlowsInProgress={updateAccountsFlowsInProgress}
              createExpectedFundsOriginsInProgress={
                createExpectedFundsOriginsInProgress
              }
              updateExpectedFundsOriginsInProgress={
                updateExpectedFundsOriginsInProgress
              }
              sourceOfWealth={sourceOfWealth}
              sourceOfIncome={sourceOfIncome}
              sourceOfWealthForIndividual={sourceOfWealthForIndividual}
              customerSourceOfFunds={customerSourceOfFunds}
              selectedCrsAndFatcaId={selectedCrsAndFatcaId}
              crsAndFatcaIsLoading={crsAndFatcaIsLoading}
              crsAndFatcaIsFetching={crsAndFatcaIsFetching}
              createCrsAndFatcaIsProgress={createCrsAndFatcaIsProgress}
              updateCrsAndFatcaInProgress={updateCrsAndFatcaInProgress}
              deleteCrsAndFatcaInProgress={deleteCrsAndFatcaInProgress}
              onDeleteCRSAndFatca={onDeleteCRSAndFatca}
              organizationControl={organizationControl}
              organizationErrors={organizationErrors}
              organizationClearErrors={organizationClearErrors}
              organizationFields={organizationFields}
              organizationSetValue={organizationSetValue}
              crsAndFatcaValues={crsAndFatcaValues}
              setCrsAndFatcaValues={setCrsAndFatcaValues}
              affilatedCompaniesValues={affilatedCompaniesValues}
              setAffilatedCompaniesValues={setAffilatedCompaniesValues}
              selectedAffilatedCompanyId={selectedAffilatedCompanyId}
              affilatedCompaniesIsLoading={affilatedCompaniesIsLoading}
              affilatedCompaniesIsFetching={affilatedCompaniesIsFetching}
              createAffilatedCompanyInProgress={
                createAffilatedCompanyInProgress
              }
              updateAffilatedCompanyInProgress={
                updateAffilatedCompanyInProgress
              }
              deleteAffilatedCompanyInProgress={
                deleteAffilatedCompanyInProgress
              }
              onDeleteAffilatedCompany={onDeleteAffilatedCompany}
            />
          )}
        {activePermissions?.includes(
          PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_LIST,
        ) && (
          <Card
            title="Risk scoring"
            isLoading={isLoading}
            className={cn.riskScoringCard}
          >
            <RiskScoring
              aml={aml}
              onUpdate={onUpdate}
              updateInProgress={requestInProgress}
              updateIsSuccess={isSuccess}
              amlUpdateStatus={amlUpdateStatus}
              calculateAllowed={activePermissions?.includes(
                PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_AUTO_ON_ACTION,
              )}
              manualScoreAllowed={activePermissions?.includes(
                PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_CREATE_ACTION,
              )}
              status={baseInformation?.status}
              isLoading={isLoading}
              users={users}
              setManualScoreSidepanelVisible={setManualScoreSidepanelVisible}
              setRiskScoreChangeLogsSidepanelVisible={
                setRiskScoreChangeLogsSidepanelVisible
              }
            />
          </Card>
        )}

        {activePermissions?.includes(
          PermissionEnum.INDIVIDUAL_CUSTOMERS_AML_LIST,
        ) && (
          <Card
            titleContent={
              <div className={cn.ongoingTitleWrapper}>
                <div className={cn.ongoingTitleLeft}>
                  <div className={cn.ongoingTitle}>Ongoing monitoring</div>
                  <button
                    type="button"
                    className={cn.ongoingShowMore}
                    onClick={() => setShowOngoing(true)}
                  >
                    Show more
                    <IconRight />
                  </button>
                </div>

                <div className={cn.ongoingTitleRight}>
                  {activePermissions?.includes(
                    PermissionEnum.INDIVIDUAL_CUSTOMERS_AML_EXPORT_ACTION,
                  ) && (
                    <button
                      type="button"
                      className={cn.ongoingExport}
                      onClick={() => setExportPdfPanelVisible(true)}
                    >
                      <IconUpload />
                      Export PDF
                    </button>
                  )}
                </div>
              </div>
            }
          >
            <div
              className={`${cn.ongoingTableWrapper} ${
                ongoingIsLoading || ongoingIsFetching ? cn.isLoading : ''
              }`}
            >
              <Table
                columns={ongoingColumns}
                data={formattedData || []}
                sort={{}}
                onSort={() => {}}
                filters={{}}
                tableBodyClassName={
                  ongoingMonitoring?.length ? '' : cn.emptyOngoing
                }
                columnsLabels={ongoingColumnsLabels}
                onFilterChange={() => {}}
                onRowClick={onOngoingRowClick}
                isLoading={ongoingIsLoading || ongoingIsFetching}
              />
            </div>
          </Card>
        )}

        <AmlAccountsTransactionStatement
          title="Red flag history"
          matchPath={ROUTES.CUSTOMERS_INDIVIDUAL_AML_CARDS}
          transactionsPath={ROUTES.CUSTOMERS_INDIVIDUAL_AML_TRANSACTIONS}
          cardsTransactionsPath={ROUTES.CUSTOMERS_INDIVIDUAL_AML_CARDS}
          customerType={CustomerType.INDIVIDUAL}
          accounts={baseInformation?.accounts}
          cards={baseInformation?.cards}
          cardsEnabled={cardsEnabled}
          sendMessage={sendMessage}
        />
        <Card title="Initial CDD">
          <div className={cn.realMonthlyVolumeIndividual}>
            <RealMonthlyVolumes
              title={`Indicated monthly turnover (${baseCurrency?.name})`}
              monthlyMaxValueIncomingPaymentRangeId={
                cddFormValues?.monthlyMaxValueIncomingPaymentRangeId
              }
              monthlyMaxValueOutgoingPaymentRangeId={
                cddFormValues?.monthlyMaxValueOutgoingPaymentRangeId
              }
              monthlyNumberIncomingTransactionsRangeId={
                cddFormValues?.monthlyNumberIncomingTransactionsRangeId
              }
              monthlyNumberOutgoingTransactionsRangeId={
                cddFormValues?.monthlyNumberOutgoingTransactionsRangeId
              }
              monthlyTurnoverRangeId={cddFormValues?.monthlyTurnoverRangeId}
              monthlyValueIncomingTransactionsRangeId={
                cddFormValues?.monthlyValueIncomingTransactionsRangeId
              }
              monthlyValueOutgoingTransactionsRangeId={
                cddFormValues?.monthlyValueOutgoingTransactionsRangeId
              }
              onChange={onChange}
              onSaveChanges={onSaveChanges}
              status={baseInformation?.status}
              saveButtonDisabled={!isDirty || updateRangesInProgress}
              requestInProgress={updateRangesInProgress}
            />
          </div>
        </Card>
      </>
    );
  }

  return (
    <div className={cn.amlContent}>
      {amlContent}
      {successMessageVisible && (
        <div className={cn.successAppliedMessage}>
          <IconSelected />
          <div>Risk Score successfully updated to the Customer</div>
        </div>
      )}
      <SidePanel
        type={SidePanelType.Customers}
        closeOnClickOutside
        isOpen={exportPdfPanelVisible}
        onClose={() => setExportPdfPanelVisible(false)}
        title="Export Ongoing monitoring"
        sidepanelExpandedKey="export-ongoing-panel"
        footer={
          <>
            <ButtonPrimary
              text={ongoingMonitoringPdfIsLoading ? 'Loading...' : 'Export'}
              onClick={() => getOngoingMonitoringPdfAction()}
              disabled={ongoingMonitoringPdfIsLoading}
              bold
            />
            <ButtonPseudo
              text="Close"
              onClick={() => setExportPdfPanelVisible(false)}
              bold
            />
          </>
        }
      >
        <div className={cn.datePickerRangeWrapper}>
          <div className={cn.datePickerRangeWrapperTitle}>Select period</div>
          <div className={cn.dateRangePickerWrapperContent}>
            <DateInputRange
              title="Select period"
              values={exportDate}
              onChangeRange={(date) =>
                setExportDate(date.map((item) => item.toISOString()))
              }
              placeholder="Select period"
              className={cn.datePickerItem}
              withoutTwoPickers
              withoutCustomSelector
            />
            <IconCalendar className={cn.iconCalendar} />
          </div>
        </div>
      </SidePanel>
      <SidePanel
        type={SidePanelType.Customers}
        closeOnClickOutside
        isOpen={ongoingItemPanelVisible}
        onClose={() => {
          setOngoingItemPanelVisible(false);
          setApplyError('');
        }}
        title="Ongoing monitoring"
        sidepanelExpandedKey="ongoing-panel"
        footer={
          <ButtonPseudo
            text="Close"
            onClick={() => {
              setOngoingItemPanelVisible(false);
              setApplyError('');
            }}
            bold
          />
        }
      >
        {ongoingSidepanelContent}
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistDataVisible}
        onClose={() => {
          setOngoingItemPanelVisible(true);
          setBlacklistDataVisible(false);
          setBlacklistData(undefined);
        }}
        title={blacklistData?.fullName as string}
        sidepanelExpandedKey="ongoing-blacklist-data"
        footer={
          <ButtonPseudo
            text="Close"
            onClick={() => {
              setOngoingItemPanelVisible(true);
              setBlacklistDataVisible(false);
              setBlacklistData(undefined);
            }}
            bold
          />
        }
      >
        <BlacklistDataContent data={blacklistData} />
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={manualScoreSidepanelVisible}
        onClose={() => {
          if (aml?.totalRiskScore === totalRiskScore) {
            setManualScoreSidepanelVisible(false);
            setTotalRiskScore(0);
            setTotalRiskScoreLevel('');
            setManualRiskScoreError('');
          } else {
            setDirtyPopupVisible(true);
          }
        }}
        title="Set Risk Score manually"
        sidepanelExpandedKey="manual-risk-score"
        errorSidepanel={manualRiskScoreError}
        footer={
          <>
            <ButtonPrimary
              text={createRiskScoreIsLoading ? 'Loading...' : 'Apply'}
              disabled={createRiskScoreIsLoading}
              onClick={createIndividualManualRiskScoreAction}
              bold
            />
            <ButtonPseudo
              text="Close"
              onClick={() => {
                if (aml?.totalRiskScore === totalRiskScore) {
                  setManualScoreSidepanelVisible(false);
                  setTotalRiskScore(0);
                  setTotalRiskScoreLevel('');
                  setManualRiskScoreError('');
                } else {
                  setDirtyPopupVisible(true);
                }
              }}
              bold
            />
          </>
        }
      >
        <>
          <div className={cn.manualRiskScoreDescription}>
            Select a Risk level or enter an exact risk score value.
          </div>
          <div className={cn.manualRiskScoreFormGroup}>
            <Select
              name="riskLevel"
              title="Risk Level"
              value={totalRiskScoreLevel}
              list={riskScoreRanges?.map((item) => ({
                name: `${item.name.charAt(0)}${item.name
                  .slice(1)
                  .toLowerCase()} (${item.rangeStart} - ${item.rangeEnd})`,
                value: item.id,
                suffix: `${item.rangeStart} - ${item.rangeEnd}`,
              }))}
              onChange={(e) => {
                setTotalRiskScoreLevel(e.target.value as string);
                setTotalRiskScore(
                  riskScoreRanges?.find((item) => item.id === e.target.value)
                    ?.rangeStart as number,
                );
              }}
              fullWidth
            />
          </div>
          <div className={cn.manualRiskScoreFormGroup}>
            <InputNumber
              name="riskScore"
              title="Risk Score"
              value={totalRiskScore}
              placeholder="Enter risk score"
              onChange={(e) => {
                clearTimeout(manualRiskScoreTimeout);
                const value =
                  Number(e.target.value) > 100 ? 100 : Number(e.target.value);

                setTotalRiskScore(value);

                manualRiskScoreTimeout = setTimeout(() => {
                  setTotalRiskScoreLevel(
                    riskScoreRanges?.find(
                      (item) =>
                        value >= item.rangeStart && value <= item.rangeEnd,
                    )?.id as string,
                  );
                }, 300);
              }}
            />
          </div>
        </>
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistItemVisible}
        onClose={() => {
          setBlacklistDataItem(undefined);
          setBlacklistItemVisible(false);
          setOngoingItemPanelVisible(true);
        }}
        title="User Details"
        sidepanelExpandedKey="ongoing-blacklist-data"
        footer={
          <ButtonPseudo
            text="Close"
            onClick={() => {
              setBlacklistDataItem(undefined);
              setBlacklistItemVisible(false);
              setOngoingItemPanelVisible(true);
            }}
            bold
          />
        }
        withoutExpand
        expanded
      >
        <BlacklistUserInfo info={blacklistItem} isLoading={false} />
      </SidePanel>
      <SidePanel
        isOpen={changeLogsPanelIsOpen}
        onClose={() => {
          setChangeLogPanelIsOpen(false);
        }}
        title="Customer change logs"
        sidepanelExpandedKey="customers-change-logs"
        type={SidePanelType.Accounts}
        footer={
          <div>
            <ButtonPseudo
              text="Close"
              onClick={() => setChangeLogPanelIsOpen(false)}
              bold
            />
          </div>
        }
        closeOnClickOutside
      >
        <>
          {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <ChangeLogs
              entityType="ORGANIZATION"
              entityId={customerId as string}
              changeLogsIsLoading={changeLogsIsLoading}
              changeLogs={changeLogs}
              activePermission={activePermissions}
              users={users}
              pagination={changeLogsPagination}
              changeLogsIsFetching={changeLogsIsFetching}
              getChangeLogs={getChangeLogsAction}
            />
          )}
          {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <PermissionDenied />
          )}
        </>
      </SidePanel>
      <AmlChangeLogs
        panelIsOpen={riskScoreChangeLogsSidepanelVisible}
        handleCloseAmlChangeLogsPanel={handleCloseAmlChangeLogsPanel}
        getAmlChangeLogs={getCustomerAmlChangeLogsAction}
        amlChangeLogsIsLoading={amlScoresHistoryIsLoading}
        amlChangeLogs={amlChangeLogs}
        amlChangeLogsPagination={amlChangeLogsPagination}
        amlScoresHistoryIsFetching={amlScoresHistoryIsFetching}
        users={users}
        type={CustomerType.INDIVIDUAL}
        organizationId={customerId}
      />
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={() => {
          setManualScoreSidepanelVisible(false);
          setTotalRiskScore(0);
          setTotalRiskScoreLevel('');
          setManualRiskScoreError('');
        }}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
      <div id="apply-all-popup" />
      <div id="signature-popup" />
    </div>
  );
};

export default Aml;
